export interface WordToken {
  readonly text: string;
  readonly type: 'same' | 'removed' | 'added';
}

interface DiffResult {
  readonly left: WordToken[];
  readonly right: WordToken[];
}

function tokenize(text: string): string[] {
  return text.split(/(\s+)/).filter((t) => t.length > 0);
}

function computeLCS(a: string[], b: string[]): number[][] {
  const m = a.length;
  const n = b.length;
  const dp: number[][] = Array.from({ length: m + 1 }, () =>
    new Array<number>(n + 1).fill(0),
  );

  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (a[i - 1] === b[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1] + 1;
      } else {
        dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
      }
    }
  }

  return dp;
}

export function wordDiff(current: string, proposed: string): DiffResult {
  const tokensA = tokenize(current);
  const tokensB = tokenize(proposed);

  const wordsA = tokensA.filter((t) => t.trim().length > 0);
  const wordsB = tokensB.filter((t) => t.trim().length > 0);

  const dp = computeLCS(wordsA, wordsB);

  // Backtrack to find LCS indices in both arrays
  const lcsMatchA = new Set<number>();
  const lcsMatchB = new Set<number>();

  let i = wordsA.length;
  let j = wordsB.length;

  while (i > 0 && j > 0) {
    if (wordsA[i - 1] === wordsB[j - 1]) {
      lcsMatchA.add(i - 1);
      lcsMatchB.add(j - 1);
      i--;
      j--;
    } else if (dp[i - 1][j] >= dp[i][j - 1]) {
      i--;
    } else {
      j--;
    }
  }

  const left: WordToken[] = wordsA.map((word, idx) => ({
    text: word,
    type: lcsMatchA.has(idx) ? ('same' as const) : ('removed' as const),
  }));

  const right: WordToken[] = wordsB.map((word, idx) => ({
    text: word,
    type: lcsMatchB.has(idx) ? ('same' as const) : ('added' as const),
  }));

  return { left, right };
}
